import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Box, Badge, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import NotificationForm from '../forms/NotificationForm';
import { getNotifications, deleteNotification } from '../../services/api.service';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import DeleteButton from '../buttons/DeleteButton';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const Notifications = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [apiNotifications, setNotifications] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getNotificationsList();
		// eslint-disable-next-line
	}, []);

	// Get notifications
	const getNotificationsList = () => {

		// Get
		getNotifications(function (response) {

			// Set notifications and remove loader
			setNotifications(response.data);
			setFilteredData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Título
			</Th>
			<Th>
				Descripción
			</Th>
			<Th>
				Fecha
			</Th>
			<Th>
				Estado
			</Th>
			<Th></Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
		>
			<td width={100}>
				{element.id}
			</td>
			<td width={120}>
				{element.title}
			</td>
			<td>
				{element.description}
			</td>
			<td>
				{element.scheduledDate ? dayjs(new Date(element.scheduledDate)).format('DD-MM-YYYY HH:mm') : 'N/A'}
			</td>
			<td>
				{element.status === 'SENT' ?
					<Badge color="green.6" variant="filled">Enviada</Badge>
				:
					<Badge color="orange.6" variant="filled">Programada</Badge>
				}
			</td>
			<td>
				<DeleteButton
					disabled={element.status === 'SENT'}
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(apiNotifications, value)
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key]?.toString().toLowerCase().includes(query))));
	}

	// Close notifications modal
	const closeModal = () => {
		// Get notifications and close modal
		getNotificationsList();
		close();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Notificación',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar esta notificación? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar Notificación', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeleteNotification(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete notification
	const triggerDeleteNotification = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteNotification(id, function () {

			// Show success
			notifications.show({
				title: 'Notificación eliminada correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				lockScroll={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Notification Form */}
				<NotificationForm
					closeModal={closeModal}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Notificaciones
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Notification */}
							<Button
								onClick={open}
								variant="filled"
							>
								Nueva Notificación
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar notificaciones"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={7}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default Notifications;
